/**
 * The Web — `DESIGN.md` §7–§10 as something you can look at and spend into.
 *
 * D §1.2 names the last phase of the loop **Become**, so the key is `B`.
 *
 * Three decisions about what this draws, and all three are about honesty:
 *
 *  · **An unworked direction looks unworked.** Reach and Root carry D
 *    §8.3–8.4's spokes, Beasts the first branching direction of the 41-point
 *    ruling (`web.ts` header — siblings on one ring are fanned, `spreadOf`);
 *    the other eleven have an identity and three Epics and nothing in
 *    between. Their spokes are drawn as empty rungs rather than filled with
 *    invented nodes, so the panel shows the eleven gaps D Appendix B.2 owes
 *    rather than hiding them.
 *  · **Every node shows what it asks the simulation for, and whether the
 *    simulation has it.** The detail card carries a `built / partial / absent`
 *    chip per effect, read from `bindings.ts`. That turns the level-up menu
 *    into the back-engineering tool as well: you can see, while spending a
 *    point, that the thing you bought has nothing behind it yet.
 *  · **A refusal says why in words.** D §6.2's history qualification is the
 *    whole reason the outer Web is interesting, and nothing in the build can
 *    answer it yet, so the panel says *what you would have to have lived*
 *    instead of greying the node out and leaving the player guessing.
 *
 * Render-only and law-9-safe by construction: the panel reads a `GodhoodWeb`
 * and calls its methods. Opening it, hovering it and reading it write nothing
 * anywhere — spending a point does, and that is a choice, not an observation.
 */

import type { Standing } from '@/sim/godhood/level';
import {
  AXES, BINDINGS, CROSSROADS, DIRECTIONS, EPIC_JOURNEY, EPIC_REQUIRES, GodhoodWeb,
  MAX_EPICS, NODES, demandedVariables, directionById, epicsOf, floorOf, nodesOf, ordinaryPointsIn,
  writtenToEpic,
  type Crossroad, type DirectionId, type Effect, type Epic, type WebNode,
} from '@/sim/godhood/index';

/* ── geometry ──────────────────────────────────────────────────────────── */

const VIEW = 1040;                       // viewBox is -520 … 520
const R_INNER = 100;                     // the awakening rung
const R_STEP = 26;                       // one position outward
const R_EPIC = 430;
const R_LABEL = 472;
const MAX_POSITION = 12;

/** Opposites face each other: seven axes, each drawn as one diameter. */
const angleOf = (d: DirectionId): number => {
  const axis = AXES.findIndex((a) => a.poles.includes(d));
  const second = AXES[axis].poles[1] === d;
  return (axis * 180) / AXES.length + (second ? 180 : 0) - 90;
};

const radiusOf = (position: number): number =>
  position >= 20 ? R_EPIC : R_INNER + (Math.min(position, MAX_POSITION) - 1) * R_STEP;

const at = (d: DirectionId, position: number, spread = 0): [number, number] => {
  const a = ((angleOf(d) + spread) * Math.PI) / 180;
  const r = radiusOf(position);
  return [Math.cos(a) * r, Math.sin(a) * r];
};

/**
 * Where a node sits when several share a ring — the branch, drawn. Siblings
 * at one position are fanned around the spoke by an angle that keeps them
 * about `SIBLING_GAP` canvas units apart at that ring's radius, so an inner
 * ring's fan is wider in degrees than an outer ring's and the dots never
 * overlap. Order is the direction's node order, so the layout is a function
 * of the data and never of a Map's iteration.
 */
const SIBLING_GAP = 21;
const spreadOf = (siblings: number, i: number, position: number): number => {
  if (siblings <= 1) return 0;
  const step = (SIBLING_GAP / radiusOf(position)) * (180 / Math.PI);
  return (i - (siblings - 1) / 2) * step;
};

const n2 = (v: number) => v.toFixed(1);

/* ── how an effect reads ───────────────────────────────────────────────── */

const effectPhrase = (e: Effect): string => {
  const pct = (v: number) => `${v >= 1 ? '+' : '−'}${Math.round(Math.abs(v - 1) * 100)}%`;
  switch (e.kind) {
    case 'multiplier': return e.value === 0 ? 'nil' : e.value >= 2 ? `×${n2(e.value)}` : pct(e.value);
    case 'cost': return `${pct(e.value)} cost`;
    case 'add': return `${e.value >= 0 ? '+' : ''}${e.value}`;
    case 'cap': return `up to ${e.value}`;
    case 'threshold': return `${e.value >= 0 ? '+' : ''}${e.value} threshold`;
    case 'rule': return 'a rule, not a number';
  }
};

const STATUS_WORD = { built: 'built', partial: 'partial', absent: 'unbuilt' } as const;

/* ── the panel ─────────────────────────────────────────────────────────── */

export interface GodhoodPanel {
  toggle(): void;
  open(): void;
  close(): void;
  readonly isOpen: boolean;
  /** Repaint after something outside changed the web (a level-up, a load). */
  refresh(): void;
}

const STORE_KEY = 'terra-godhood';

/**
 * Persisted outside the `terra-` snapshot namespace on purpose. The Web is
 * per-deity state and belongs in `GodState` and therefore in the snapshot —
 * but **nothing in the simulation reads it yet**, and bumping
 * `SNAPSHOT_VERSION` costs a twenty-minute prehistory regeneration and every
 * existing save. It moves inside the moment the first system reads
 * `web.modifier(...)`, and that move is the version bump it deserves.
 */
export function loadWeb(): GodhoodWeb {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return GodhoodWeb.fromSnapshot(JSON.parse(raw));
  } catch { /* corrupt or blocked: a fresh deity is the right fallback */ }
  return new GodhoodWeb();
}

const saveWeb = (w: GodhoodWeb) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(w.toSnapshot())); } catch { /* ignore */ }
};

type Selection =
  | { kind: 'node'; node: WebNode }
  | { kind: 'epic'; epic: Epic }
  | { kind: 'direction'; id: DirectionId }
  | { kind: 'crossroad'; road: Crossroad }
  | null;

/**
 * `onOpen` runs immediately before the panel paints, every way it can be
 * opened — the menu, `B`, or `open()` from anywhere else. `main.ts` uses it to
 * re-derive Godhood Level from the Chronicle, and the callback exists rather
 * than the caller doing it at its own call sites because `B` is handled in
 * here and would otherwise show a stale level exactly when a player looks.
 */
/**
 * WHY THIS LEVEL — the four fields `standingOf` computed and nobody read.
 *
 * The rule this obeys is the one the whole progression design turns on: a god
 * levels because the WORLD changed, never because the god was busy. So `acts`
 * is shown and is deliberately never a numerator — law 25, "the same act on
 * the same subject in the same conditions must approach zero" — and
 * `barrenActs` is shown beside it because *what came to nothing* is the single
 * most useful thing a player can be told about their own play. A god that has
 * cast four hundred times for two enduring deeds is being told something true
 * and actionable, and the alternative is a level with no visible cause.
 *
 * `contributions` is folded to its distinct KINDS rather than listed: the list
 * is one row per enduring strand and is unbounded, and the shape that matters
 * is how many different ways this god has changed the world — `NOVELTY_DECAY`
 * means the tenth burn is worth a fraction of the first, so breadth is the
 * actionable half and a roster of individual deeds is not.
 */
const levelWhy = (s: Standing): string => {
  const kinds = new Set(s.contributions.map((c) => c.kind)).size;
  const enduring = s.contributions.length;
  const dim = 'color:var(--dim);font-size:9px';
  return `<br><span style="${dim}">`
    + (enduring === 0
      ? 'nothing you have done has endured yet — the world levels you, not the casting'
      : `${enduring} deed${enduring === 1 ? '' : 's'} the world kept`
        + `, in ${kinds} ${kinds === 1 ? 'way' : 'different ways'}`)
    + `</span>`
    + `<br><span style="${dim}">${s.acts} act${s.acts === 1 ? '' : 's'}`
    + (s.barrenActs > 0
      ? `, ${s.barrenActs} of which came to nothing`
      : s.acts > 0 ? ', none of them wasted' : '')
    + `</span>`;
};


export function createGodhoodPanel(
  web: GodhoodWeb, onChange?: () => void, onOpen?: () => void,
  /**
   * What the world remembers of this god, read fresh when the panel opens.
   *
   * `standingOf` computes six fields and the build read exactly one of them —
   * `level` — so `total`, `contributions`, `acts` and `barrenActs` were
   * computed every time the panel opened and thrown away. They are the only
   * answer the game has to *why am I this level*, which is the question a
   * progression surface exists to answer and the one this panel could not.
   */
  standing?: () => Standing,
): GodhoodPanel {
  const style = document.createElement('style');
  style.textContent = `
    #godhood { position:fixed; inset:0; z-index:60; display:none;
      background:rgba(10,11,9,.90); backdrop-filter:blur(10px); }
    #godhood.open { display:block; }
    #godhood .wrap { position:absolute; inset:0; display:grid;
      grid-template-columns:1fr 320px; }
    #godhood .web { position:relative; display:flex; align-items:center;
      justify-content:center; min-width:0; }
    #godhood svg { width:min(100%,100vh); height:100%; max-height:100vh; }

    #godhood .top { position:absolute; top:22px; left:26px; pointer-events:none; }
    #godhood .top h2 { font:italic 400 22px/1 'Instrument Serif', serif;
      letter-spacing:.04em; color:var(--ink); }
    #godhood .top .become { margin-top:10px; max-width:360px; font-size:11px; line-height:1.45;
      color:var(--gold); }
    #godhood .top .become:empty { display:none; }
    #godhood .top .become b { font-weight:600; }
    #godhood .top .become [data-become] { text-decoration:underline dotted; cursor:pointer; }
    #godhood .top .sub { margin-top:6px; font-size:9px; letter-spacing:.22em;
      text-transform:uppercase; color:var(--dim); }
    #godhood .points { margin-top:14px; font-size:11px; color:var(--ink);
      font-variant-numeric:tabular-nums; }
    #godhood .points b { color:var(--gold); font-weight:400; font-size:15px; }

    #godhood .card { display:flex; flex-direction:column; }
    #godhood .cardbody { flex:1 1 auto; overflow-y:auto; min-height:0; }
    #godhood .level { flex:0 0 auto; margin-top:14px; padding-top:12px;
      border-top:1px solid var(--line); font-size:9px; color:var(--dim);
      letter-spacing:.1em; line-height:1.7; }
    #godhood .level button { padding:2px 9px; color:var(--dim);
      border:1px solid var(--line); border-radius:9px; margin-left:4px; }
    #godhood .level button:hover { color:var(--gold); border-color:var(--gold); }
    #godhood .level em { font-style:normal; color:var(--ink);
      font-variant-numeric:tabular-nums; }

    /* the spokes */
    #godhood .axis { stroke:var(--line); stroke-width:1; }
    #godhood .axis.unworked { stroke-dasharray:3 7; }
    #godhood .ring { fill:none; stroke:rgba(233,227,210,.06); }
    #godhood .core { font:italic 400 30px 'Instrument Serif', serif; fill:var(--gold); }
    #godhood .corelabel { font-size:7px; letter-spacing:.3em; fill:var(--dim); }
    #godhood .dname { font-size:11px; letter-spacing:.14em; text-transform:uppercase;
      fill:var(--dim); }
    #godhood .dname.invested { fill:var(--gold); }
    #godhood .dpts { font-size:9px; fill:var(--dim); font-variant-numeric:tabular-nums; }

    #godhood .node { cursor:pointer; }
    #godhood .node circle { stroke:var(--dim); stroke-width:1.3; }
    #godhood .node .rank-off { fill:rgba(10,11,9,.92); }
    #godhood .node .rank-on { fill:var(--gold); stroke:var(--gold); }
    #godhood .node .hit { fill:transparent; stroke:none; }
    #godhood .node.open .rank-off { stroke:var(--ink); }
    #godhood .node.locked .rank-off { stroke:rgba(233,227,210,.18); }
    #godhood .node.part .rank-off { stroke:var(--gold); }
    #godhood .node.sel circle { stroke-width:2.4; }
    #godhood .node.sel .rank-off { stroke:var(--ink); }
    /* a node history has earned and the god has not yet taken: the Become
       moment, drawn as a ring that breathes */
    #godhood .node.earned .rank-off { stroke:var(--gold); animation:earned 2.4s ease-in-out infinite; }
    @keyframes earned { 0%,100% { stroke-opacity:.45 } 50% { stroke-opacity:1 } }
    #godhood .node:hover .rank-off { stroke:var(--ink); }

    #godhood .epic { cursor:pointer; }
    #godhood .epic polygon { fill:rgba(10,11,9,.9); stroke:var(--dim); stroke-width:1.4; }
    #godhood .epic.open polygon { stroke:var(--ink); }
    #godhood .epic.chosen polygon { fill:var(--gold); stroke:var(--gold); }
    #godhood .epic.sel polygon { stroke:var(--ink); stroke-width:2.6; }
    #godhood .epic:hover polygon { stroke:var(--ink); }

    #godhood .cross { cursor:pointer; }
    #godhood .cross line { stroke:rgba(201,162,39,.16); stroke-width:1; }
    #godhood .cross.taken line { stroke:var(--gold); stroke-width:1.6; }

    /* the card */
    #godhood .card { border-left:1px solid var(--line); padding:24px 22px;
      font-size:11px; color:var(--dim); line-height:1.65; }
    #godhood .card .where { font-size:9px; letter-spacing:.2em; text-transform:uppercase;
      color:var(--dim); }
    #godhood .card h3 { font:italic 400 19px/1.25 'Instrument Serif', serif;
      color:var(--ink); margin:6px 0 2px; }
    #godhood .card .tier { font-size:9px; letter-spacing:.16em; text-transform:uppercase;
      color:var(--gold); }
    #godhood .card p { margin:11px 0; }
    #godhood .card p.meaning { color:var(--ink); }
    #godhood .card .eff { margin:9px 0; padding-left:10px;
      border-left:1px solid var(--line); }
    #godhood .card .eff .v { color:var(--ink); font-variant-numeric:tabular-nums; }
    #godhood .card .eff .var { font-size:9px; color:var(--dim); word-break:break-all; }
    #godhood .card .eff .obs { font-style:italic; }
    #godhood .card .chip { display:inline-block; font-size:8px; letter-spacing:.1em;
      text-transform:uppercase; padding:1px 6px; border-radius:8px; margin-left:6px;
      border:1px solid var(--line); }
    #godhood .card .chip.built { color:#7FA86B; border-color:rgba(127,168,107,.4); }
    #godhood .card .chip.partial { color:var(--gold); border-color:rgba(201,162,39,.4); }
    #godhood .card .chip.absent { color:#B5674E; border-color:rgba(181,103,78,.4); }
    #godhood .card .gate { margin:12px 0; padding:9px 11px; border-radius:8px;
      background:rgba(181,103,78,.08); border:1px solid rgba(181,103,78,.22);
      color:#C98C74; font-style:italic; }
    #godhood .card .acts { display:flex; gap:6px; margin-top:16px; }
    #godhood .card .acts button { padding:5px 13px; border:1px solid var(--line);
      border-radius:10px; font-size:10px; letter-spacing:.1em; text-transform:uppercase;
      color:var(--dim); }
    #godhood .card .acts button.go { color:var(--gold); border-color:rgba(201,162,39,.45); }
    #godhood .card .acts button.go:hover { background:rgba(201,162,39,.12); }
    #godhood .card .acts button:disabled { opacity:.35; cursor:default; }
    #godhood .card .note { margin-top:20px; padding-top:14px;
      border-top:1px solid var(--line); font-size:9px; line-height:1.7; }
    #godhood .card .permanent { color:#C98C74; }
    #godhood .card .empty { font-style:italic; }
  `;
  document.head.appendChild(style);

  const el = document.createElement('div');
  el.id = 'godhood';
  el.innerHTML = `
    <div class="wrap">
      <div class="web">
        <div class="top">
          <h2>Godhood</h2>
          <div class="sub">what this deity has become</div>
          <div class="points"></div>
          <div class="become"></div>
        </div>
        <svg viewBox="${-VIEW / 2} ${-VIEW / 2} ${VIEW} ${VIEW}"></svg>
      </div>
      <div class="card"><div class="cardbody"></div><div class="level"></div></div>
    </div>`;
  document.body.appendChild(el);

  const svg = el.querySelector('svg')!;
  const card = el.querySelector<HTMLElement>('.cardbody')!;
  const pointsEl = el.querySelector<HTMLElement>('.points')!;
  const becomeEl = el.querySelector<HTMLElement>('.become')!;
  const levelEl = el.querySelector<HTMLElement>('.level')!;
  let sel: Selection = null;

  const SVGNS = 'http://www.w3.org/2000/svg';
  const make = <K extends keyof SVGElementTagNameMap>(
    tag: K, attrs: Record<string, string | number>,
  ): SVGElementTagNameMap[K] => {
    const n = document.createElementNS(SVGNS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
    return n;
  };

  /* ── painting the web ─────────────────────────────────────────────── */

  const paintWeb = () => {
    svg.textContent = '';

    for (const r of [R_INNER, radiusOf(6), radiusOf(MAX_POSITION), R_EPIC]) {
      svg.appendChild(make('circle', { class: 'ring', cx: 0, cy: 0, r }));
    }

    // the centre is the deity: D §7 puts it there with the universal
    // faculties, before it has become anything in particular
    const lv = make('text', {
      class: 'core', x: 0, y: -6, 'text-anchor': 'middle', 'dominant-baseline': 'central',
    });
    lv.textContent = String(web.level);
    svg.appendChild(lv);
    const lvl = make('text', {
      class: 'corelabel', x: 0, y: 14, 'text-anchor': 'middle', 'dominant-baseline': 'central',
    });
    lvl.textContent = 'GODHOOD';
    svg.appendChild(lvl);

    // crossroads first, so they sit under the nodes
    for (const c of CROSSROADS) {
      const [a, b] = c.between;
      const [ax, ay] = at(a, 6);
      const [bx, by] = at(b, 6);
      const g = make('g', { class: `cross${web.hasCrossroad(c.id) ? ' taken' : ''}` });
      g.appendChild(make('line', { x1: ax, y1: ay, x2: bx, y2: by }));
      g.addEventListener('click', () => { sel = { kind: 'crossroad', road: c }; paint(); });
      svg.appendChild(g);
    }

    for (const d of DIRECTIONS) {
      const worked = d.worked;
      const [ex, ey] = at(d.id, 20);
      svg.appendChild(make('line', {
        class: `axis${worked ? '' : ' unworked'}`, x1: 0, y1: 0, x2: ex, y2: ey,
      }));

      // the rungs the doc has not written, drawn as absence
      if (!worked) {
        for (let p = 2; p <= MAX_POSITION; p++) {
          const [x, y] = at(d.id, p);
          svg.appendChild(make('circle', { cx: x, cy: y, r: 2, fill: 'rgba(233,227,210,.10)' }));
        }
      }

      // siblings on one ring are fanned; count them once per direction
      const ringOf = new Map<number, WebNode[]>();
      for (const node of nodesOf(d.id)) {
        const list = ringOf.get(node.position) ?? [];
        list.push(node);
        ringOf.set(node.position, list);
      }
      for (const node of nodesOf(d.id)) {
        const taken = web.ranksIn(node.id);
        const can = web.canSpend(node.id);
        const cls = taken >= node.ranks ? 'taken'
          : taken > 0 ? 'part'
          : can.ok ? 'open' : 'locked';
        // …and the Become moment: a gated node whose gate history has opened
        const earned = !!node.qualifies && taken === 0 && web.lived(node.id);
        const ring = ringOf.get(node.position)!;
        const [x, y] = at(d.id, node.position, spreadOf(ring.length, ring.indexOf(node), node.position));
        const g = make('g', {
          class: `node ${cls}${earned ? ' earned' : ''}${sel?.kind === 'node' && sel.node.id === node.id ? ' sel' : ''}`,
        });
        // Ranks are depth, so they are drawn as depth: one ring per rank,
        // filling from the inside out. A three-rank node is visibly a deeper
        // thing than a one-rank node before you have read a word of it, and
        // no text has to survive being 8 px wide on a 1040-unit canvas.
        for (let i = node.ranks; i >= 1; i--) {
          svgRing(g, x, y, 5 + (i - 1) * 3.2, i <= taken);
        }
        // a generous invisible target — the visible dot is 8 units across
        g.appendChild(make('circle', { class: 'hit', cx: x, cy: y, r: 15 }));
        g.addEventListener('click', () => { sel = { kind: 'node', node }; paint(); });
        svg.appendChild(g);
      }

      // the epic end: three fanned choices once the direction holds its
      // forty, one diamond before. `writtenToEpic` keeps a thin direction's
      // diamond closed — Reach and Root hold 18 of the 40 and say so on click.
      const chosen = web.epicOf(d.id);
      const trio = epicsOf(d.id);
      const mastered = writtenToEpic(d.id) && web.ordinaryIn(d.id) >= EPIC_REQUIRES;
      const shown = chosen ? [chosen] : mastered ? trio : [];
      if (!shown.length) {
        const [x, y] = at(d.id, 20);
        const g = make('g', { class: 'epic locked' });
        g.appendChild(make('polygon', { points: diamond(x, y, 6) }));
        g.addEventListener('click', () => { sel = { kind: 'direction', id: d.id }; paint(); });
        svg.appendChild(g);
      } else {
        shown.forEach((e, i) => {
          const spread = shown.length === 1 ? 0 : (i - 1) * 5.2;
          const [x, y] = at(d.id, 20, spread);
          const isChosen = chosen?.id === e.id;
          const g = make('g', {
            class: `epic ${isChosen ? 'chosen' : 'open'}${sel?.kind === 'epic' && sel.epic.id === e.id ? ' sel' : ''}`,
          });
          g.appendChild(make('polygon', { points: diamond(x, y, isChosen ? 9 : 7) }));
          g.addEventListener('click', () => { sel = { kind: 'epic', epic: e }; paint(); });
          svg.appendChild(g);
        });
      }

      // the rim label
      const a = (angleOf(d.id) * Math.PI) / 180;
      const lx = Math.cos(a) * R_LABEL;
      const ly = Math.sin(a) * R_LABEL;
      const anchor = Math.abs(lx) < 40 ? 'middle' : lx > 0 ? 'start' : 'end';
      const points = web.pointsIn(d.id);
      const label = make('text', {
        class: `dname${points ? ' invested' : ''}`, x: lx, y: ly,
        'text-anchor': anchor, 'dominant-baseline': 'central',
      });
      label.textContent = d.name;
      label.style.cursor = 'pointer';
      label.addEventListener('click', () => { sel = { kind: 'direction', id: d.id }; paint(); });
      svg.appendChild(label);

      if (points) {
        const pl = make('text', {
          class: 'dpts', x: lx, y: ly + 13,
          'text-anchor': anchor, 'dominant-baseline': 'central',
        });
        pl.textContent = `${points}`;
        svg.appendChild(pl);
      }
    }
  };

  // function declarations, not consts: these are used by paintWeb above, and
  // a hot-module reload that re-evaluates the module top-down can otherwise
  // land in the temporal dead zone and abort a repaint half-drawn
  function diamond(x: number, y: number, r: number): string {
    return `${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`;
  }

  /** One rank, drawn as a ring. Filled means taken. */
  function svgRing(g: SVGElement, x: number, y: number, r: number, filled: boolean): void {
    g.appendChild(make('circle', {
      cx: x, cy: y, r, class: filled ? 'rank-on' : 'rank-off',
    }));
  }

  /* ── the card ─────────────────────────────────────────────────────── */

  const chip = (variable: string) => {
    const s = BINDINGS[variable]?.status ?? 'absent';
    return `<span class="chip ${s}">${STATUS_WORD[s]}</span>`;
  };

  const effectsHtml = (effects: readonly Effect[]) => effects.map((e) => `
    <div class="eff">
      <div><span class="v">${effectPhrase(e)}</span>${chip(e.variable)}</div>
      <div class="var">${e.variable}${BINDINGS[e.variable]?.status !== 'built'
        ? ` — ${BINDINGS[e.variable]?.where ?? 'undeclared'}` : ''}</div>
      <div class="obs">“${e.observed}”</div>
    </div>`).join('');

  const paintCard = () => {
    if (!sel) {
      const v = demandedVariables();
      const absent = v.filter((x) => (BINDINGS[x]?.status ?? 'absent') === 'absent').length;
      card.innerHTML = `
        <div class="where">the web</div>
        <h3>Fourteen ways to be</h3>
        <p>Seven tensions, each with two answers. Opposites face each other; a
           deity that goes far in one direction will not go far in its
           opposite, and that is the point of the shape.</p>
        <p>A direction is a body of choices, not a ladder: each layer opens on
           how deep you already are, and holds more than the next asks for.
           Forty points in a direction open its Epic; the forty-first, if you
           have lived what it asks, answers permanently. A god may answer
           <b>twice</b> in a life, never three times.</p>
        <p class="empty">Choose a node to see what it asks the world for.</p>
        <div class="note">
          <p><b>Beasts</b> is written to that shape. <b>Reach</b> and <b>Root</b>
             carry D §8.3–8.4's older, thinner spokes — 18 of the 40 an Epic
             asks for. The other eleven show their Awakening and their three
             Epics; the empty rungs between are what
             <b>DESIGN.md Appendix B.2</b> owes.</p>
          <p>The Web asks the simulation for <b>${v.length}</b> things by name.
             <b>${absent}</b> of them have nothing behind them yet —
             <code>npx tsx tools/godhood.ts</code> prints which.</p>
        </div>`;
      return;
    }

    if (sel.kind === 'direction') {
      const d = directionById(sel.id);
      const spent = web.pointsIn(d.id);
      const capacity = ordinaryPointsIn(d.id);
      const axis = AXES.find((a) => a.poles.includes(d.id))!;
      // where the emphasis has gone, when the direction has Currents — a
      // reading of the god's own investment, never a gate
      const currents = (d.currents ?? [])
        .map((c) => ({ c, n: web.currentPointsIn(d.id, c) }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n);
      const emphasis = currents.length
        ? `<p>Your ${d.name} runs mostly to <b>${currents[0].c}</b>` +
          (currents.length > 1 ? `, then ${currents.slice(1).map((x) => x.c).join(', ')}` : '') +
          ` — an emphasis you have arrived at, not chosen.</p>`
        : d.currents?.length
          ? `<p>Three currents run through it — ${d.currents.join(' · ')} — and nothing
             asks you to choose among them. Invest, and see where you have leaned.</p>`
          : '';
      card.innerHTML = `
        <div class="where">${axis.name}</div>
        <h3>${d.name}</h3>
        <div class="tier">${spent} spent · ${capacity} to be had · Epic at ${EPIC_JOURNEY}</div>
        <p class="meaning">${d.identity}</p>
        <p>${axis.question}</p>
        ${emphasis}
        ${d.worked ? '' : `<div class="gate">DESIGN.md has not written this
          direction's nodes yet — only its identity and its three Epics
          (Appendix B.2). Its spoke is empty because the design is, not
          because the panel is.</div>`}
        ${d.worked && !writtenToEpic(d.id) ? `<div class="gate">Written to the older
          20-point shape: ${capacity} ordinary points, where an Epic asks for
          ${EPIC_REQUIRES}. Its outer nodes are reachable; its Epic is not, until
          it is written deeper.</div>` : ''}
        <div class="note"><p>Its Epics: ${epicsOf(d.id).map((e) => e.name).join(' · ')}.
          One of the three, permanently, as the ${EPIC_JOURNEY}st point of a direction
          that holds ${EPIC_REQUIRES}${epicsOf(d.id).some((e) => e.qualifies) ? ' — and only for a god who has lived what it asks' : ''}.</p></div>`;
      return;
    }

    if (sel.kind === 'crossroad') {
      const c = sel.road;
      const v = web.canTakeCrossroad(c.id);
      const has = web.hasCrossroad(c.id);
      card.innerHTML = `
        <div class="where">${directionById(c.between[0]).name} + ${directionById(c.between[1]).name}</div>
        <h3>${c.name}</h3>
        <div class="tier">crossroad · ${c.cost} points</div>
        <p class="meaning">${c.represents}</p>
        <p>Needs ${c.requiresEach} points in each of the two directions it joins.</p>
        ${c.effects.length ? effectsHtml(c.effects) : `<div class="gate">DESIGN.md §10
          gives crossroads names and adjacencies, not statistics. This one has
          nothing to apply yet.</div>`}
        ${has ? '' : v.ok ? '' : `<div class="gate">${v.says}</div>`}
        <div class="acts">
          <button class="${v.ok ? 'go' : ''}" data-take-cross ${v.ok ? '' : 'disabled'}>
            ${has ? 'taken' : 'take'}</button>
        </div>`;
      card.querySelector('[data-take-cross]')?.addEventListener('click', () => {
        if (web.takeCrossroad(c.id).ok) commit();
      });
      return;
    }

    if (sel.kind === 'epic') {
      const e = sel.epic;
      const chosen = web.epicOf(e.direction);
      const v = web.canChooseEpic(e.direction, e.id);
      card.innerHTML = `
        <div class="where">${directionById(e.direction).name} · epic${e.current ? ` · the ${e.current} current` : ''}</div>
        <h3>${e.name}</h3>
        <div class="tier">${e.identity}</div>
        ${effectsHtml(e.effects)}
        ${chosen?.id === e.id
          ? `<div class="gate permanent">This is what your ${directionById(e.direction).name}
              means, and it cannot be changed.</div>`
          : chosen
            ? `<div class="gate">${directionById(e.direction).name} has already
                answered — ${chosen.name}. The answer is permanent.</div>`
            : v.ok
              ? `<div class="gate permanent">Permanent. D §8.2: an Epic is not
                  respec-able, in this world or the next — and it is one of the
                  ${MAX_EPICS === 2 ? 'two' : MAX_EPICS} you will ever hold.</div>`
              : v.why === 'unqualified'
                ? `<div class="gate"><b>History has not earned this.</b><br>You must ${v.says}.</div>`
                : `<div class="gate">${v.says}</div>`}
        <div class="acts">
          <button class="${v.ok ? 'go' : ''}" data-epic ${v.ok ? '' : 'disabled'}>
            ${chosen?.id === e.id ? 'chosen' : 'answer, permanently'}</button>
        </div>`;
      card.querySelector('[data-epic]')?.addEventListener('click', () => {
        if (web.chooseEpic(e.direction, e.id).ok) commit();
      });
      return;
    }

    const node = sel.node;
    const d = directionById(node.direction);
    const taken = web.ranksIn(node.id);
    const can = web.canSpend(node.id);
    const back = web.ranksIn(node.id) > 0;
    const floor = floorOf(node);
    card.innerHTML = `
      <div class="where">${d.name}${node.current ? ` · the ${node.current} current` : ''}</div>
      <h3>${node.name}</h3>
      <div class="tier">${node.tier}${node.ranks > 1 ? ` · ${taken} of ${node.ranks}` : ''}${
        floor > 0 ? ` · opens at ${floor} in ${d.name}` : ''}</div>
      <p class="meaning">${node.meaning}</p>
      ${node.effects.length ? effectsHtml(node.effects) : `<div class="gate">
        DESIGN.md has not specified what this direction's nodes do
        (Appendix B.2), so this rung asks the simulation for nothing yet.</div>`}
      ${node.qualifies && !can.ok && can.why === 'unqualified'
        ? `<div class="gate"><b>History has not earned this.</b><br>You must ${node.qualifies.describes}.</div>`
        : ''}
      ${!can.ok && can.why !== 'unqualified' && can.why !== 'maxed'
        ? `<div class="gate">${can.says}</div>` : ''}
      <div class="acts">
        <button class="${can.ok ? 'go' : ''}" data-spend ${can.ok ? '' : 'disabled'}>invest</button>
        <button data-refund ${back ? '' : 'disabled'}>give back</button>
      </div>
      ${node.qualifies ? `<div class="note"><p>Outer nodes are gated on
        <b>history</b>, not points (D §6.2) — on what your hand actually did
        and what the world actually became, never on what you watched. Only
        consequence counts.</p></div>` : ''}`;

    card.querySelector('[data-spend]')?.addEventListener('click', () => {
      if (web.spend(node.id).ok) commit();
    });
    card.querySelector('[data-refund]')?.addEventListener('click', () => {
      if (web.refund(node.id).ok) commit();
    });
  };

  /* ── chrome ───────────────────────────────────────────────────────── */

  const paintChrome = () => {
    /**
     * The shape line used to quote D §8.1's "a mature god near 36%" against
     * a 280-point Web. Under the 41-point ruling the shape is said in the
     * ruling's own terms: how many directions this god has entered, and how
     * many of the two Epics it will ever hold it has answered.
     */
    const entered = DIRECTIONS.filter((d) => web.pointsIn(d.id) > 0).length;
    pointsEl.innerHTML =
      `<b>${web.pointsFree}</b> unspent · ${web.pointsSpent} of ${web.pointsTotal} spent`
      + `<br><span style="color:var(--dim);font-size:9px">`
      + `${entered} of ${DIRECTIONS.length} directions entered · ${web.epicCount} of ${MAX_EPICS} Epics answered</span>`;

    /**
     * THE PANEL USED TO SAY THE LEVEL SYSTEM WAS UNBUILT, next to a real one.
     *
     * It printed *"dev scaffolding — D §6 levels on consequential history,
     * which is unbuilt"* and offered −/+/+10 buttons that set `web.level` by
     * hand. Both halves had been false since the Chronicle landed:
     * `main.ts`'s `refreshLevel` derives the level from `standingOf` — from
     * what the world actually remembers of this god — and it runs whenever the
     * panel opens, so the buttons were also *futile*, overwritten by the real
     * reading a moment later.
     *
     * What replaces them is what the reading already knew and nobody asked
     * for. `standingOf` returns six fields and one was read.
     */
    const st = standing?.();
    /**
     * THE BECOME MOMENT (`PLAYABLE.md` P5). `Tab` recognises that something
     * has become possible because of how the god acted: every gated node
     * whose qualification history now answers and which the god has not yet
     * taken is named here, above the level, whether or not the god can yet
     * afford it — the recognition is the point, and the price is the node's
     * own business. Nothing is pushed: the ring breathes on the web and the
     * sentence says what was lived, in the world's terms.
     */
    const become = NODES.filter((n) => n.qualifies && web.ranksIn(n.id) === 0 && web.lived(n.id));
    // under the panel's head, where the eye lands — not at the foot of the
    // right column, where the first build put it and where a screenshot of
    // the open panel found it below two paragraphs of the Web's own prose
    becomeEl.innerHTML = become.length
      ? `<b>something has become possible.</b> `
        + become.map((n) => `<span data-become="${n.id}">${n.name}</span>`).join(' · ')
        + `<br><span style="color:var(--dim);font-size:9px">you ${become[0].qualifies!.describes}</span>`
      : '';
    levelEl.innerHTML =
      `<span>godhood level <em>${web.level}</em></span>`
      + (st ? levelWhy(st) : '');
    for (const span of becomeEl.querySelectorAll<HTMLElement>('[data-become]')) {
      span.style.cursor = 'pointer';
      span.addEventListener('click', () => {
        const node = NODES.find((n) => n.id === span.dataset.become);
        if (node) { sel = { kind: 'node', node }; paint(); }
      });
    }
  };

  const paint = () => { paintWeb(); paintCard(); paintChrome(); };
  const commit = () => { saveWeb(web); paint(); onChange?.(); };

  el.addEventListener('pointerdown', (e) => e.stopPropagation());

  const setOpen = (open: boolean) => {
    el.classList.toggle('open', open);
    if (open) { onOpen?.(); paint(); }
  };

  // Deliberately NO key handler here. Become is **Tab**, and main.ts owns it —
  // as it owns Escape — because one key with one owner is the only arrangement
  // that survives: this panel used to bind `B` for itself while `ui/settings.ts`
  // bound Tab for itself, and the chrome had as many keyboard listeners as it
  // had panels.
  paint();

  return {
    toggle: () => setOpen(!el.classList.contains('open')),
    open: () => setOpen(true),
    close: () => setOpen(false),
    get isOpen() { return el.classList.contains('open'); },
    refresh: paint,
  };
}
